import React from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

const Features: React.FC = () => {
  const { t } = useTranslation();
  
  return (
    <div className="bg-gray-100 dark:bg-gray-900 min-h-screen">
      <Helmet>
        <title>{t('metadata.features.title')}</title>
        <meta name="description" content={t('metadata.features.description')} />
      </Helmet>

      <main className="container mx-auto px-4 py-8 max-w-4xl">
        <h1 className="text-4xl font-bold mb-8 text-gray-800 dark:text-white">Free IPTV Player & HLS Stream Player Features</h1>
        
        <section className="mb-12">
          <h2 className="text-2xl font-semibold mb-4 text-gray-700 dark:text-gray-200">The Ultimate Online M3U & IPTV Player</h2>
          <p className="text-gray-600 dark:text-gray-300 mb-4">
            Welcome to the most comprehensive free IPTV player available online. Our platform allows you to easily manage, play, and store your M3U playlists in the cloud. 
            Whether you're looking for an HLS player, DASH player, or complete M3U player solution, this guide will help you make the most of our features.
          </p>
        </section>

        <section className="mb-12">
          <h2 className="text-2xl font-semibold mb-6 text-gray-700 dark:text-gray-200">Adding Content to Your IPTV & HLS Player</h2>
          
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-6 mb-8">
            <h3 className="text-xl font-semibold mb-4 text-gray-700 dark:text-gray-200">1. Drag and Drop M3U8 Files</h3>
            <div className="relative w-full h-64 md:h-80 bg-gray-100 dark:bg-gray-700 rounded-lg overflow-hidden mb-4">
              <img 
                src="/images/drag-drop.png" 
                alt={t('altText.dragDrop')} 
                className="w-full h-full object-contain"
              />
            </div>
            <p className="text-gray-600 dark:text-gray-300">
              Simply drag your M3U8 playlist file into the designated area or click to upload. Our player supports all standard M3U8 formats, 
              making it the perfect choice for managing your IPTV playlists online.
            </p>
          </div>

          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-6 mb-8">
            <h3 className="text-xl font-semibold mb-4 text-gray-700 dark:text-gray-200">2. Import from URL</h3>
            <div className="relative w-full h-64 md:h-80 bg-gray-100 dark:bg-gray-700 rounded-lg overflow-hidden mb-4">
              <img 
                src="/images/import-url.png" 
                alt={t('altText.importUrl')} 
                className="w-full h-full object-contain"
              />
            </div>
            <p className="text-gray-600 dark:text-gray-300">
              Have a playlist URL? Easily import M3U playlists by pasting the URL into our import field and clicking "Import Playlist". 
              This feature streamlines the process of adding new content to your collection.
            </p>
          </div>

          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-6 mb-8">
            <h3 className="text-xl font-semibold mb-4 text-gray-700 dark:text-gray-200">3. Add Single Streams</h3>
            <div className="relative w-full h-64 md:h-80 bg-gray-100 dark:bg-gray-700 rounded-lg overflow-hidden mb-4">
              <img 
                src="/images/add-stream.png" 
                alt={t('altText.addStream')} 
                className="w-full h-full object-contain"
              />
            </div>
            <p className="text-gray-600 dark:text-gray-300">
              Add individual streams by entering the stream URL, channel name, and optional details like group and logo URL. 
              This feature is perfect for creating and managing custom IPTV playlists online.
            </p>
          </div>
        </section>

        <section className="mb-12">
          <h2 className="text-2xl font-semibold mb-6 text-gray-700 dark:text-gray-200">Managing Your Channels</h2>
          
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-6 mb-8">
            <h3 className="text-xl font-semibold mb-4 text-gray-700 dark:text-gray-200">Edit Mode and Channel Management</h3>
            <div className="relative w-full h-64 md:h-80 bg-gray-100 dark:bg-gray-700 rounded-lg overflow-hidden mb-4">
              <img 
                src="/images/edit-mode.png" 
                alt={t('altText.editMode')} 
                className="w-full h-full object-contain"
              />
            </div>
            <p className="text-gray-600 dark:text-gray-300">Toggle edit mode to modify your channels. Click the edit icon to enter edit mode, where you can:</p>
            <ul className="list-disc ml-6 text-gray-600 dark:text-gray-300">
              <li>Modify channel names and URLs</li>
              <li>Organize channels into groups</li>
              <li>Update channel logos</li>
              <li>Delete unwanted channels</li>
            </ul>
          </div>
        </section>

        <section className="mb-12">
          <h2 className="text-2xl font-semibold mb-6 text-gray-700 dark:text-gray-200">Saving and Exporting</h2>
          
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-6 mb-8">
            <h3 className="text-xl font-semibold mb-4 text-gray-700 dark:text-gray-200">Cloud Save Feature</h3>
            <div className="relative w-full h-64 md:h-80 bg-gray-100 dark:bg-gray-700 rounded-lg overflow-hidden mb-4">
              <img 
                src="/images/cloud-save.png" 
                alt={t('altText.saveCloud')} 
                className="w-full h-full object-contain"
              />
            </div>
            <p className="text-gray-600 dark:text-gray-300">
              Save your M3U playlists to the cloud for access anywhere. This feature requires a free account and ensures your playlists 
              are always available when you need them.
            </p>
          </div>

          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-6">
            <h3 className="text-xl font-semibold mb-4 text-gray-700 dark:text-gray-200">Export Functionality</h3>
            <div className="relative w-full h-64 md:h-80 bg-gray-100 dark:bg-gray-700 rounded-lg overflow-hidden mb-4">
              <img 
                src="/images/export.png" 
                alt={t('altText.exportPlaylist')} 
                className="w-full h-full object-contain"
              />
            </div>
            <p className="text-gray-600 dark:text-gray-300">
              Export your playlists in M3U format to use with other players or share with others. Click the export button to download your playlist file.
            </p>
          </div>
        </section>

        <section className="bg-blue-50 dark:bg-blue-900/20 rounded-lg p-6 mb-12">
          <h2 className="text-2xl font-semibold mb-4 text-blue-800 dark:text-blue-300">Pro Tips for Our M3U Player</h2>
          <ul className="list-disc ml-6 text-gray-700 dark:text-gray-300 space-y-2">
            <li>Organize your channels into groups for better navigation</li>
            <li>Use the search function to quickly find specific channels</li>
            <li>Regularly save your playlists to the cloud to prevent data loss</li>
            <li>Export backups of your playlists periodically</li>
          </ul>
        </section>

        <section className="bg-gray-50 dark:bg-gray-800/50 rounded-lg p-6">
          <h2 className="text-2xl font-semibold mb-4 text-gray-800 dark:text-gray-200">Need Help?</h2>
          <p className="text-gray-600 dark:text-gray-300">
            Having trouble with M3U playback issues or need assistance with our features? Check out our{' '}
            <Link 
              to="/m3u-hls-player-faq" 
              className="text-blue-500 hover:text-blue-600 dark:text-blue-400 dark:hover:text-blue-300 transition-colors"
            >
              {t('faq.title', 'FAQ')}
            </Link>
            {' '}or{' '}
            <Link 
              to="/contact" 
              className="text-blue-500 hover:text-blue-600 dark:text-blue-400 dark:hover:text-blue-300 transition-colors"
            >
              contact our support team
            </Link>
            {' '}for help with managing your IPTV playlists online.
          </p>
        </section>
      </main>
    </div>
  );
};

export default Features; 